/**
 * MomentumEngine — the operational intelligence core of the Deal Room.
 *
 * Synthesizes the live `/api/deals/{id}/momentum` payload into a calm,
 * cinematic command-centre view: Pulse, Narrative, Flow Signals,
 * Responsiveness, Readiness and Deal Rescue suggestions.
 *
 * Visual identity matches the existing right-rail FlowIntelligencePanel
 * (dark gradient, violet accent, restrained tones). Designed for
 * 1-second understanding — the agent should grasp the deal's
 * operational state before reading a single sentence.
 */
import React, { useState } from "react";
import {
  Activity, AlertTriangle, ChevronDown, ChevronUp, Clock,
  FileText, KeyRound, Link2, LifeBuoy, ShieldAlert, Sparkles,
  TrendingUp, Users, Inbox, Home,
} from "lucide-react";

const ICONS = {
  clock: Clock, file: FileText, user: Users, key: KeyRound,
  shield: ShieldAlert, chain: Link2, spark: Sparkles, inbox: Inbox, home: Home,
};

/* ── Severity & momentum tones (restrained, premium) ─────────────────── */
const SEVERITY = {
  low:      { fg: "#A7F3D0", dot: "#10B981", chip: "rgba(16,185,129,0.14)",  border: "rgba(16,185,129,0.28)" },
  medium:   { fg: "#FCD34D", dot: "#F59E0B", chip: "rgba(245,158,11,0.14)",  border: "rgba(245,158,11,0.28)" },
  high:     { fg: "#FCA5A5", dot: "#EF4444", chip: "rgba(239,68,68,0.16)",   border: "rgba(239,68,68,0.32)" },
  critical: { fg: "#FECACA", dot: "#EF4444", chip: "rgba(239,68,68,0.22)",   border: "rgba(239,68,68,0.40)" },
};

const PULSE = {
  strong:           { fg: "#A7F3D0", dot: "#10B981", chip: "rgba(16,185,129,0.16)",  border: "rgba(16,185,129,0.32)", ring: "0 0 0 0 rgba(16,185,129,0)" },
  healthy:          { fg: "#C4B5FD", dot: "#8B5CF6", chip: "rgba(139,92,246,0.16)",  border: "rgba(139,92,246,0.32)", ring: "0 0 0 0 rgba(139,92,246,0)" },
  minor_friction:   { fg: "#FCD34D", dot: "#F59E0B", chip: "rgba(245,158,11,0.14)",  border: "rgba(245,158,11,0.28)", ring: "0 0 0 0 rgba(245,158,11,0)" },
  cooling:          { fg: "#FCD34D", dot: "#F59E0B", chip: "rgba(245,158,11,0.14)",  border: "rgba(245,158,11,0.28)", ring: "0 0 0 0 rgba(245,158,11,0)" },
  at_risk:          { fg: "#FCA5A5", dot: "#EF4444", chip: "rgba(239,68,68,0.16)",   border: "rgba(239,68,68,0.32)", ring: "0 0 0 0 rgba(239,68,68,0)" },
  recovery:         { fg: "#A5F3FC", dot: "#06B6D4", chip: "rgba(6,182,212,0.14)",   border: "rgba(6,182,212,0.28)", ring: "0 0 0 0 rgba(6,182,212,0)" },
};

const RESP_TONE = {
  Excellent:    { fg: "#A7F3D0", dot: "#10B981" },
  Healthy:      { fg: "#C4B5FD", dot: "#8B5CF6" },
  Delayed:      { fg: "#FCD34D", dot: "#F59E0B" },
  Slow:         { fg: "#FCA5A5", dot: "#F97316" },
  Unresponsive: { fg: "#FECACA", dot: "#EF4444" },
};

const ROLE_LABEL = {
  solicitor: "Solicitor",
  buyer: "Buyer",
  seller: "Seller",
  broker: "Broker",
};

/* ── Pulse chip with restrained glow ─────────────────────────────────── */
const PulseChip = ({ pulse }) => {
  const s = PULSE[pulse?.state] || PULSE.healthy;
  return (
    <span
      data-testid={`momentum-pulse-${pulse?.state || "unknown"}`}
      className="inline-flex items-center gap-1.5 px-2.5 h-7 rounded-full text-[10.5px] font-bold whitespace-nowrap shrink-0"
      style={{ background: s.chip, border: `1px solid ${s.border}`, color: s.fg, letterSpacing: "0.01em" }}
      title={`Transaction Pulse · ${pulse?.label}`}
    >
      <span className="relative inline-flex">
        <span className="w-1.5 h-1.5 rounded-full" style={{ background: s.dot }} />
        {(pulse?.state === "strong" || pulse?.state === "recovery") && (
          <span className="absolute inset-0 rounded-full animate-ping"
                style={{ background: s.dot, opacity: 0.55 }} />
        )}
      </span>
      <Activity size={10} />
      {pulse?.label || "—"}
    </span>
  );
};

/* ── Readiness ring + blockers ───────────────────────────────────────── */
const ReadinessRow = ({ readiness }) => {
  const v = Math.max(0, Math.min(100, readiness?.score || 0));
  const r = 22;
  const c = 2 * Math.PI * r;
  const dash = (v / 100) * c;
  const color = v >= 75 ? "#10B981" : v >= 55 ? "#8B5CF6" : v >= 35 ? "#F59E0B" : "#EF4444";
  const conf = readiness?.confidence || "medium";
  const confLabel = conf === "high" ? "High confidence" : conf === "medium" ? "Medium confidence" : "Low confidence";
  return (
    <div
      className="flex items-center gap-3 px-3.5 py-3 rounded-xl"
      style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)" }}
      data-testid="momentum-readiness"
    >
      <div className="relative w-[52px] h-[52px] shrink-0">
        <svg viewBox="0 0 52 52" className="w-full h-full -rotate-90">
          <circle cx="26" cy="26" r={r} stroke="rgba(148,163,184,0.18)" strokeWidth="3.4" fill="none" />
          <circle cx="26" cy="26" r={r} stroke={color} strokeWidth="3.4" fill="none"
                  strokeLinecap="round" strokeDasharray={`${dash} ${c}`} />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="text-[13px] font-bold tabular-nums text-white">{v}%</span>
        </div>
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-[10px] uppercase tracking-[0.20em] font-bold" style={{ color: "#C4B5FD" }}>
          Exchange Readiness
        </p>
        <p className="text-[12px] font-bold text-white leading-tight mt-0.5">
          {readiness?.blocker_count || 0} blocker{(readiness?.blocker_count || 0) === 1 ? "" : "s"} remaining
        </p>
        <p className="text-[10.5px] mt-0.5" style={{ color: "rgba(148,163,184,0.85)" }}>{confLabel}</p>
      </div>
    </div>
  );
};

/* ── Flow Signal card ────────────────────────────────────────────────── */
const FlowSignalCard = ({ signal, onAction }) => {
  const [open, setOpen] = useState(false);
  const sev = SEVERITY[signal.severity] || SEVERITY.medium;
  const Icon = ICONS[signal.icon] || Sparkles;

  const handleClick = (a) => {
    if (typeof onAction === "function") onAction(signal, a);
  };

  return (
    <li
      data-testid={`momentum-signal-${signal.id}`}
      data-severity={signal.severity}
      className="rounded-xl"
      style={{
        background: "rgba(255,255,255,0.025)",
        border: "1px solid rgba(255,255,255,0.06)",
      }}
    >
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="w-full px-3 py-2.5 flex items-start gap-3 text-left rounded-xl hover:bg-white/[0.015] transition-colors"
        data-testid={`momentum-signal-toggle-${signal.id}`}
      >
        <span className="w-7 h-7 rounded-lg flex items-center justify-center shrink-0 mt-0.5 relative"
              style={{ background: sev.chip, color: sev.fg, border: `1px solid ${sev.border}` }}>
          <Icon size={11} />
        </span>
        <div className="flex-1 min-w-0">
          <p className="text-[11.5px] font-bold text-white leading-tight truncate">{signal.title}</p>
          <p className="text-[10px] mt-0.5 leading-snug" style={{ color: "rgba(148,163,184,0.85)" }}>
            {signal.owner} · {signal.waiting_label}
          </p>
        </div>
        <span className="inline-flex items-center gap-1 text-[9.5px] font-bold uppercase tracking-[0.14em] shrink-0 mt-1"
              style={{ color: sev.fg }}>
          <span className="w-1.5 h-1.5 rounded-full" style={{ background: sev.dot }} />
          {signal.severity}
        </span>
        {open ? <ChevronUp size={11} className="text-slate-400 shrink-0 mt-1" />
              : <ChevronDown size={11} className="text-slate-400 shrink-0 mt-1" />}
      </button>

      {open && (
        <div className="px-3 pb-3 -mt-0.5 cf-settle-dark">
          <div className="ml-10 pt-1 pb-0.5 border-t border-white/[0.05] mt-1">
            {signal.impact && (
              <p className="text-[10.5px] mt-2 leading-snug" style={{ color: "rgba(203,213,225,0.85)" }}>
                <span className="font-bold text-white/90">Impact · </span>{signal.impact}
              </p>
            )}
            <p className="text-[10.5px] mt-1.5 leading-snug" style={{ color: "rgba(203,213,225,0.95)" }}>
              <span className="font-bold text-white/90">Next · </span>{signal.next_action}
            </p>
            {signal.quick_actions?.length > 0 && (
              <div className="flex flex-wrap items-center gap-1.5 mt-2.5">
                {signal.quick_actions.map((a) => (
                  <button
                    key={a.id}
                    type="button"
                    onClick={(e) => { e.stopPropagation(); handleClick(a); }}
                    data-testid={`momentum-signal-action-${signal.id}-${a.id}`}
                    className="h-7 px-2.5 rounded-md text-[10.5px] font-semibold inline-flex items-center gap-1 transition-colors"
                    style={
                      a.kind === "primary"
                        ? {
                            background: "linear-gradient(135deg, rgba(124,58,237,0.85), rgba(99,102,241,0.85))",
                            color: "#FFFFFF",
                            border: "1px solid rgba(167,139,250,0.45)",
                            boxShadow: "0 8px 18px -10px rgba(99,102,241,0.55)",
                          }
                        : a.kind === "secondary"
                          ? {
                              background: "rgba(255,255,255,0.04)",
                              color: "#E2E8F0",
                              border: "1px solid rgba(255,255,255,0.10)",
                            }
                          : {
                              background: "transparent",
                              color: "rgba(203,213,225,0.85)",
                              border: "1px solid rgba(255,255,255,0.06)",
                            }
                    }
                  >
                    {a.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </li>
  );
};

/* ── Responsiveness chips ────────────────────────────────────────────── */
const ResponsivenessGrid = ({ responsiveness }) => {
  if (!responsiveness?.length) return null;
  return (
    <div
      className="grid grid-cols-2 gap-1.5"
      data-testid="momentum-responsiveness"
    >
      {responsiveness.map((r) => {
        const t = RESP_TONE[r.label] || RESP_TONE.Healthy;
        return (
          <div
            key={r.role}
            data-testid={`momentum-resp-${r.role}`}
            className="px-2.5 py-1.5 rounded-lg"
            style={{ background: "rgba(255,255,255,0.025)", border: "1px solid rgba(255,255,255,0.06)" }}
          >
            <p className="text-[9.5px] uppercase tracking-[0.14em] font-bold leading-tight" style={{ color: "rgba(148,163,184,0.85)" }}>
              {ROLE_LABEL[r.role] || r.role}
            </p>
            <p className="text-[11px] font-bold mt-0.5 inline-flex items-center gap-1.5 leading-tight" style={{ color: t.fg }}>
              <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: t.dot }} />
              {r.label}
            </p>
          </div>
        );
      })}
    </div>
  );
};

/* ── Rescue suggestions ──────────────────────────────────────────────── */
const RescueBlock = ({ suggestions }) => {
  if (!suggestions?.length) return null;
  return (
    <div
      className="rounded-xl px-3.5 py-3"
      style={{
        background: "linear-gradient(180deg, rgba(124,58,237,0.10) 0%, rgba(124,58,237,0.04) 100%)",
        border: "1px solid rgba(167,139,250,0.22)",
      }}
      data-testid="momentum-rescue"
    >
      <div className="flex items-center gap-2 mb-2">
        <LifeBuoy size={12} className="text-violet-300" />
        <p className="text-[10px] font-bold uppercase tracking-[0.20em]" style={{ color: "#C4B5FD" }}>Deal Rescue</p>
      </div>
      <ul className="space-y-1.5">
        {suggestions.map((s) => (
          <li key={s.id} data-testid={`momentum-rescue-${s.id}`}>
            <p className="text-[11.5px] font-bold text-white leading-tight">{s.title}</p>
            <p className="text-[10.5px] leading-snug mt-0.5" style={{ color: "rgba(203,213,225,0.85)" }}>{s.rationale}</p>
          </li>
        ))}
      </ul>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════════════
   Public component
   ─────────────────────────────────────────────────────────────────── */
export const MomentumEngine = ({ momentum, loading, onAction }) => {
  const isLoading = loading || !momentum;
  const pulse = momentum?.pulse;

  return (
    <section
      className="rounded-2xl p-5 overflow-hidden relative shrink-0"
      style={{
        background: "linear-gradient(180deg, #1A1635 0%, #0F0E22 100%)",
        border: "1px solid rgba(139,92,246,0.28)",
        boxShadow: "0 16px 36px -16px rgba(99,102,241,0.45), inset 0 1px 0 rgba(255,255,255,0.06)",
      }}
      data-testid="momentum-engine"
    >
      {/* Soft ambient bloom */}
      <span
        aria-hidden="true"
        className="absolute -top-16 -right-12 w-[180px] h-[180px] rounded-full blur-3xl pointer-events-none"
        style={{ background: "radial-gradient(circle, rgba(139,92,246,0.20), transparent 70%)" }}
      />

      {/* Header */}
      <div className="flex items-center justify-between gap-2 mb-3 relative">
        <div className="flex items-center gap-2 min-w-0">
          <span
            className="w-7 h-7 rounded-md inline-flex items-center justify-center shrink-0"
            style={{ background: "rgba(139,92,246,0.22)", color: "#C4B5FD" }}
          >
            <TrendingUp size={12} />
          </span>
          <p className="text-[10.5px] font-bold uppercase tracking-[0.14em] text-white/80 truncate">
            Momentum Engine
          </p>
        </div>
        {!isLoading && <PulseChip pulse={pulse} />}
      </div>

      {isLoading ? (
        <div className="space-y-2 cf-pulse" data-testid="momentum-loading">
          <div className="h-12 rounded-lg" style={{ background: "rgba(255,255,255,0.04)" }} />
          <div className="h-8 rounded-lg" style={{ background: "rgba(255,255,255,0.03)" }} />
          <div className="h-8 rounded-lg" style={{ background: "rgba(255,255,255,0.03)" }} />
          <div className="h-8 rounded-lg" style={{ background: "rgba(255,255,255,0.03)" }} />
        </div>
      ) : (
        <div className="space-y-3 relative">
          {/* Narrative — calm, executive-level summary */}
          <p
            data-testid="momentum-narrative"
            className="text-[11.5px] leading-relaxed"
            style={{ color: "rgba(226,232,240,0.92)" }}
          >
            {momentum.narrative}
          </p>

          {/* Readiness */}
          <ReadinessRow readiness={momentum.readiness} />

          {/* Flow Signals */}
          {momentum.flow_signals?.length > 0 && (
            <div>
              <p className="text-[10px] font-bold uppercase tracking-[0.20em] mb-1.5" style={{ color: "#C4B5FD" }}>
                Flow Signals · {momentum.flow_signals.length}
              </p>
              <ul className="space-y-1.5" data-testid="momentum-signals">
                {momentum.flow_signals.map((s) => (
                  <FlowSignalCard key={s.id} signal={s} onAction={onAction} />
                ))}
              </ul>
            </div>
          )}

          {/* Empty signals = healthy state */}
          {momentum.flow_signals?.length === 0 && (
            <div
              className="rounded-xl px-3.5 py-3 flex items-center gap-2.5"
              style={{ background: "rgba(16,185,129,0.08)", border: "1px solid rgba(16,185,129,0.20)" }}
              data-testid="momentum-no-blockers"
            >
              <span className="w-7 h-7 rounded-lg inline-flex items-center justify-center"
                    style={{ background: "rgba(16,185,129,0.16)", color: "#A7F3D0", border: "1px solid rgba(16,185,129,0.32)" }}>
                <Sparkles size={12} />
              </span>
              <div>
                <p className="text-[11.5px] font-bold text-white">No active blockers</p>
                <p className="text-[10.5px]" style={{ color: "rgba(167,243,208,0.85)" }}>Operational momentum is clear — keep cadence.</p>
              </div>
            </div>
          )}

          {/* Responsiveness */}
          <div>
            <p className="text-[10px] font-bold uppercase tracking-[0.20em] mb-1.5" style={{ color: "#C4B5FD" }}>
              Responsiveness
            </p>
            <ResponsivenessGrid responsiveness={momentum.responsiveness} />
          </div>

          {/* Rescue suggestions — only when present */}
          <RescueBlock suggestions={momentum.rescue_suggestions} />
        </div>
      )}

      <style>{`
        .cf-settle-dark { animation: cf-settle-dark 360ms cubic-bezier(0.22, 1, 0.36, 1) both; }
        @keyframes cf-settle-dark {
          from { opacity: 0; transform: translateY(-3px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </section>
  );
};

/* ── Compact Momentum strip (for Flow Centre / pipeline cards) ───────── */
export const MomentumStrip = ({ momentum, compact = false }) => {
  if (!momentum) return null;
  const pulse = momentum.pulse;
  const readiness = momentum.readiness;
  const blockers = readiness?.blocker_count || 0;
  const sigCount = momentum.flow_signals?.length || 0;
  const longest = momentum.flow_signals?.[0];
  const s = PULSE[pulse?.state] || PULSE.healthy;

  return (
    <div
      data-testid="momentum-strip"
      className={`flex items-center gap-2 ${compact ? "text-[10.5px]" : "text-[11px]"}`}
    >
      <span
        className="inline-flex items-center gap-1 px-2 h-5 rounded-full font-bold tracking-tight"
        style={{ background: s.chip, border: `1px solid ${s.border}`, color: s.fg }}
      >
        <span className="w-1 h-1 rounded-full" style={{ background: s.dot }} />
        {pulse?.label || "—"}
      </span>
      {blockers > 0 && (
        <span className="inline-flex items-center gap-1 font-semibold" style={{ color: "#FCD34D" }}>
          <AlertTriangle size={10} />
          {blockers} blocker{blockers === 1 ? "" : "s"}
        </span>
      )}
      {sigCount > 0 && longest?.title && (
        <span className="truncate min-w-0" style={{ color: "rgba(148,163,184,0.85)" }}>
          · {longest.title}{longest.waiting_days ? ` (${longest.waiting_days}d)` : ""}
        </span>
      )}
    </div>
  );
};
